<script setup lang="ts">
import ModalContainer from '@/components/modal/modal-container.vue'
import ModalTitle from '@/components/modal/modal-title.vue'
import InputText from '@/components/input-text.vue'
import { faMagnifyingGlass, faX } from '@fortawesome/pro-regular-svg-icons'
import { computed, ref } from 'vue'
import ModalContent from '@/components/modal/modal-content.vue'
import { format } from 'date-fns/format'
import ModalFooter from '@/components/modal/modal-footer.vue'
import ButtonSecondary from '@/components/button-secondary.vue'
import { FontAwesomeIcon } from '@fortawesome/vue-fontawesome'
import DataTableCell from '@/components/data-table-cell.vue'
import DataTableHeader from '@/components/data-table-header.vue'
import DataTable from '@/components/data-table.vue'
import { useQueries, useQuery } from '@tanstack/vue-query'
import { YieldQueries } from '@/features/assets/queries'
import { UserQueries } from '@/shared/queries'
import LoaderInline from '@/shared/components/LoaderInline.vue'
import { formatISO } from 'date-fns/formatISO'
import ErrorBlock from '@/shared/components/ErrorBlock.vue'

const props = defineProps<{ assetCode: string }>()

const { data: logs, isLoading: logsAreLoading, isError } = useQuery(YieldQueries.activityLog(props.assetCode))
const search = ref('')
const dateFormatToUse = 'dd MMM yyyy @ HH:mm'

const userDetails = useQueries({
  queries: computed(
    () => Array.from(
      new Set(logs.value?.map(x => x.userId) ?? [])
    ).map(
      userId => UserQueries.fetch(userId)
    )
  )
})

const isLoading = computed(
  () => logsAreLoading.value || userDetails.value.some(x => x.isLoading)
)

const userMap = computed(
  () => userDetails.value.filter(x => !x.isLoading && !x.isError).reduce(
    (acc, value) => {
      acc[value.data!.id] = value.data!.name

      return acc
    },
    { } as Record<string, string>
  )
)

const records = computed(
  () => {
    const searchValueNormalized = search.value.toLowerCase().trim()
    const recordsNormalized = logs.value ?? []

    if (search.value === '') {
      return recordsNormalized
    }

    return recordsNormalized.filter(
      x => (
        x.description.toLowerCase().includes(searchValueNormalized) ||
        x.userId.toLowerCase().includes(searchValueNormalized) ||
        (userMap.value[x.userId]?.toLowerCase() ?? '').includes(searchValueNormalized) ||
        format(x.timestamp, dateFormatToUse).toLowerCase().includes(searchValueNormalized)
      )
    )
  }
)

</script>

<template>
  <ModalContainer v-slot="{ close }" :width="725">
    <ModalTitle class="d-flex justify-content-between">
      <div>
        Activity - Featherwood Retirement Village
        <br>
        <small>(Asset: {{ assetCode }} | Project: 2651)</small>
      </div>
      <InputText placeholder="Search" v-model:value.trim="search">
        <template #suffix>
          <FontAwesomeIcon :icon="faMagnifyingGlass" />
        </template>
      </InputText>
    </ModalTitle>
    <ModalContent>
      <div v-if="isLoading" class="text-center">
        <LoaderInline loading class="me-2" />
        Loading...
      </div>
      <ErrorBlock v-else-if="isError" class="text-center" color="red">
        Encountered an error attempting to load activity logs.
      </ErrorBlock>
      <DataTable v-else :records="records">
        <template #headers>
          <DataTableHeader>Activity</DataTableHeader>
          <DataTableHeader>User</DataTableHeader>
          <DataTableHeader>Date</DataTableHeader>
        </template>
        <template #record="{ record }">
          <DataTableCell style="white-space: preserve-breaks">{{ record.description }}</DataTableCell>
          <DataTableCell>{{ userMap[record.userId] || 'Unknown' }}</DataTableCell>
          <DataTableCell class="text-nowrap" :title="formatISO(record.timestamp)">{{ format(record.timestamp, dateFormatToUse) }}</DataTableCell>
        </template>
      </DataTable>
    </ModalContent>
    <ModalFooter class="text-end">
      <ButtonSecondary :icon="faX" @click="close">Close</ButtonSecondary>
    </ModalFooter>
  </ModalContainer>
</template>

<style scoped lang="scss">

</style>
